import React, { useEffect } from 'react';
import { useParams, Outlet, Navigate, useLocation } from 'react-router-dom';
import Container from '@/components/ui/Container';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import { useProjectNavigation } from '@/hooks/goal-planner/use-optimized-navigation';
import { useProjectAgentThread } from '@/hooks/goal-planner/use-project-agent-thread';
import ProjectTabNavigation from './ProjectTabNavigation';
import { Loader2, FolderOpen, ClipboardPlus, FilePlus2, ImagePlus, Microscope } from 'lucide-react';
import { trackProjectActivity } from '@/utils/project-navigation-cache';
import { useAuth } from '@/contexts/auth';
import { ProjectOutletContext } from '@/types/project';

/**
 * ProjectLayout - Shared layout for all project routes
 * Provides unified navigation, data loading, and error handling
 */
const ProjectLayout: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const location = useLocation();
  
  // Get project navigation data (project + goal + pages)
  const { data: navigationData, isLoading, error } = useProjectNavigation(projectId || '');
  const project = navigationData?.project;
  const goal = navigationData?.goal;
  
  // Get agent thread for project chat
  const { data: agentThreadId } = useProjectAgentThread(projectId || '');
  
  // Track project activity for smart navigation
  useEffect(() => {
    if (projectId && user?.id && project) {
      trackProjectActivity(projectId, user.id);
    }
  }, [projectId, user?.id, project]);
  
  // Store task, page, image, and research creation handlers from outlet context
  const [taskCreationHandler, setTaskCreationHandler] = React.useState<(() => void) | undefined>();
  const [pageCreationHandler, setPageCreationHandler] = React.useState<(() => void) | undefined>();
  const [imageCreationHandler, setImageCreationHandler] = React.useState<(() => void) | undefined>();
  const [researchCreationHandler, setResearchCreationHandler] = React.useState<(() => void) | undefined>();
  
  // Functions to register creation handlers from child components
  const registerTaskCreationHandler = React.useCallback((handler: (() => void) | undefined) => {
    setTaskCreationHandler(() => handler);
  }, []);
  
  const registerPageCreationHandler = React.useCallback((handler: (() => void) | undefined) => {
    setPageCreationHandler(() => handler);
  }, []);
  
  const registerImageCreationHandler = React.useCallback((handler: (() => void) | undefined) => {
    setImageCreationHandler(() => handler);
  }, []);
  
  const registerResearchCreationHandler = React.useCallback((handler: (() => void) | undefined) => {
    setResearchCreationHandler(() => handler);
  }, []);
  
  // Determine which action to show based on current route
  const getCurrentPageType = () => {
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];
    
    if (lastSegment === 'resources') return 'resources';
    if (lastSegment === 'images') return 'images';
    if (lastSegment === 'pages' || location.pathname.includes('/page/')) return 'pages';
    if (lastSegment === 'tasks') return 'tasks';
    return null;
  };
  
  const currentPageType = getCurrentPageType();
  
  // Context value to pass to children
  const outletContext: ProjectOutletContext = {
    project,
    goal,
    agentThreadId,
    navigationData,
    onCreateTask: taskCreationHandler,
    registerTaskCreationHandler,
    onCreatePage: pageCreationHandler,
    registerPageCreationHandler,
    onCreateImage: imageCreationHandler,
    registerImageCreationHandler,
    onCreateResearch: researchCreationHandler,
    registerResearchCreationHandler
  };
  
  // Determine which primary action to show
  const getPrimaryAction = () => {
    return undefined;
  };
  
  // Loading state
  if (isLoading) {
    return (
      <Container>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-purple-500" />
            <p className="text-gray-600 dark:text-gray-300">Loading project...</p>
          </div>
        </div>
      </Container>
    );
  }
  
  // Error state
  if (error || !project) {
    console.error('[ProjectLayout] Project loading failed:', { error, projectId, navigationData });
    return (
      <Container>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <FolderOpen className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-gray-100">
              Project Not Found
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              The project you're looking for doesn't exist or you don't have access to it.
            </p>
            {/* Debug info - remove in production */}
            <div className="text-xs text-gray-500 mb-4">
              Project ID: {projectId}<br/>
              Error: {error?.message || 'No error message'}<br/>
              Navigation Data: {navigationData ? 'Present' : 'Not present'}
            </div>
            <Navigate to="/project-manager" replace />
          </div>
        </div>
      </Container>
    );
  }
  
  return (
    <Container>
      {/* Project Header */}
      <DashboardHeader 
        title={project.title}
        description={project.metadata?.description || "Manage your project tasks, pages, and resources"}
        icon={<FolderOpen className="h-6 w-6" />}
        primaryAction={getPrimaryAction()}
      />
      
      {/* Project Navigation Tabs */}
      <ProjectTabNavigation 
        projectId={projectId || ''}
        goalId={goal?.id}
        agentThreadId={agentThreadId}
        project={project}
        goal={goal}
      />
      
      {/* Route-specific content */}
      <div className="mt-3">
        <Outlet context={outletContext} />
      </div>
    </Container>
  );
};

export default ProjectLayout; 